import React, { useState, useEffect } from 'react';
import { fetchProfitReportAP } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

const todayIso = new Date().toISOString().split('T')[0];
const money = n => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const intFmt = n => Number(n || 0).toLocaleString('en-US');

export default function ProfitReportAP() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(todayIso);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchProfitReportAP(toApiDate(start), toApiDate(end))
      .then(r => setRows(Array.isArray(r) ? r : (r?.data ?? [])))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const tot = rows.reduce((a, r) => ({
    orders: a.orders + Number(r.numberOrders || 0),
    reship: a.reship + Number(r.numberOrdersReShip || 0),
    gross: a.gross + Number(r.grossTotal || 0),
    cost: a.cost + Number(r.totalCost || 0),
    net: a.net + Number(r.netProfit || 0),
  }), { orders: 0, reship: 0, gross: 0, cost: 0, net: 0 });
  const avgProfit = tot.orders ? tot.net / tot.orders : 0;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Profit Report (AP)</h2>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
          Ship-date profit from the Admin Portal profit table (matches the legacy Admin Portal Profit Report).
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Date Range (Ship Date)</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateRangeFilter start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
          <button onClick={load} className="btn btn-primary" style={{ fontSize: 11 }}>Run Report</button>
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
          {[
            { label: 'Orders', value: intFmt(tot.orders) },
            { label: 'Gross Total', value: money(tot.gross) },
            { label: 'Total Cost', value: money(tot.cost) },
            { label: 'Net Profit', value: money(tot.net), highlight: true },
          ].map(({ label, value, highlight }) => (
            <div key={label} className="card" style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: highlight ? 'var(--orange)' : 'var(--navy)' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-header">By Ship Date {rows.length > 0 && `(${rows.length.toLocaleString()} days)`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No data found for selected period.</div>
        )}
        {rows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship Date</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Orders</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>ReShip</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Gross Total</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Total Cost</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Net Profit</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Avg Profit</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                    <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 600 }}>{r.shipDate ? new Date(r.shipDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'right' }}>{intFmt(r.numberOrders)}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--gray-500)' }}>{intFmt(r.numberOrdersReShip)}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'right' }}>{money(r.grossTotal)}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--gray-700)' }}>{money(r.totalCost)}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600, color: Number(r.netProfit) >= 0 ? 'var(--navy)' : 'var(--red)' }}>{money(r.netProfit)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--orange)' }}>{money(r.averageProfit)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--gray-300)', fontWeight: 700, background: 'var(--peach)' }}>
                  <td style={{ padding: '8px 12px', color: 'var(--navy)' }}>Total</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right' }}>{intFmt(tot.orders)}</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right' }}>{intFmt(tot.reship)}</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right' }}>{money(tot.gross)}</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right' }}>{money(tot.cost)}</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right' }}>{money(tot.net)}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--orange)' }}>{money(avgProfit)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
